"use client";

import { TrendingUp, TrendingDown, ArrowUpDown } from "lucide-react";
import { useState } from "react";

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/lib/components/table";

interface AppUsageData {
	app: string;
	model: string;
	requestCount: number;
	percentOfApp: number;
}

interface AppTrendData {
	app: string;
	currentWeek: number;
	previousWeek: number;
	growthPercent: number;
}

interface AppModelTableProps {
	data: AppUsageData[];
	trendData?: AppTrendData[];
	isLoading?: boolean;
}

type SortField = "app" | "requests" | "growth";
type SortOrder = "asc" | "desc";

export function AppModelTable({
	data,
	trendData,
	isLoading,
}: AppModelTableProps) {
	const [sortField, setSortField] = useState<SortField>("requests");
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

	if (isLoading) {
		return (
			<div className="flex h-[400px] items-center justify-center">
				<div className="text-sm text-muted-foreground">Loading data...</div>
			</div>
		);
	}

	if (!data || data.length === 0) {
		return (
			<div className="flex h-[400px] items-center justify-center">
				<div className="text-sm text-muted-foreground">No data available</div>
			</div>
		);
	}

	// Group by app and calculate totals
	const appStats = new Map<
		string,
		{
			app: string;
			models: Set<string>;
			totalRequests: number;
			growth: number;
		}
	>();

	data.forEach((item) => {
		const existing = appStats.get(item.app) || {
			app: item.app,
			models: new Set(),
			totalRequests: 0,
			growth: 0,
		};
		existing.models.add(item.model);
		existing.totalRequests += item.requestCount;
		const trend = trendData?.find((t) => t.app === item.app);
		existing.growth = trend?.growthPercent ?? 0;
		appStats.set(item.app, existing);
	});

	let sortedApps = Array.from(appStats.values());

	// Sort
	sortedApps.sort((a, b) => {
		let aVal: number | string = 0;
		let bVal: number | string = 0;

		switch (sortField) {
			case "app":
				aVal = a.app;
				bVal = b.app;
				break;
			case "requests":
				aVal = a.totalRequests;
				bVal = b.totalRequests;
				break;
			case "growth":
				aVal = a.growth;
				bVal = b.growth;
				break;
		}

		if (typeof aVal === "string") {
			return sortOrder === "asc"
				? aVal.localeCompare(bVal as string)
				: (bVal as string).localeCompare(aVal);
		}

		return sortOrder === "asc"
			? (aVal as number) - (bVal as number)
			: (bVal as number) - (aVal as number);
	});

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortOrder(sortOrder === "asc" ? "desc" : "asc");
		} else {
			setSortField(field);
			setSortOrder("desc");
		}
	};

	const SortIcon = ({ field }: { field: SortField }) => {
		if (sortField !== field) {
			return <ArrowUpDown className="h-4 w-4 opacity-40" />;
		}
		return sortOrder === "asc" ? (
			<ArrowUpDown className="h-4 w-4" />
		) : (
			<ArrowUpDown className="h-4 w-4 rotate-180" />
		);
	};

	return (
		<div className="rounded-lg border bg-card overflow-hidden">
			<Table>
				<TableHeader className="bg-muted/50">
					<TableRow>
						<TableHead
							className="cursor-pointer hover:bg-muted/80 transition-colors"
							onClick={() => handleSort("app")}
						>
							<div className="flex items-center gap-2">
								App
								<SortIcon field="app" />
							</div>
						</TableHead>
						<TableHead
							className="cursor-pointer hover:bg-muted/80 transition-colors text-right"
							onClick={() => handleSort("requests")}
						>
							<div className="flex items-center justify-end gap-2">
								Requests
								<SortIcon field="requests" />
							</div>
						</TableHead>
						<TableHead
							className="cursor-pointer hover:bg-muted/80 transition-colors text-right"
							onClick={() => handleSort("growth")}
						>
							<div className="flex items-center justify-end gap-2">
								Growth
								<SortIcon field="growth" />
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedApps.map((item) => (
						<TableRow
							key={item.app}
							className="hover:bg-muted/50 transition-colors"
						>
							<TableCell className="font-medium">{item.app}</TableCell>
							<TableCell className="text-right font-semibold">
								{(item.totalRequests / 1000).toFixed(1)}k
							</TableCell>
							<TableCell className="text-right">
								<div
									className={`flex items-center justify-end gap-1 font-medium ${
										item.growth >= 0
											? "text-status-ok-title"
											: "text-status-error-title"
									}`}
								>
									{item.growth >= 0 ? (
										<TrendingUp className="h-4 w-4" />
									) : (
										<TrendingDown className="h-4 w-4" />
									)}
									<span>{Math.abs(item.growth).toFixed(2)}%</span>
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
